import { useCallback, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import {
  patientSupportingDocumentsService,
  type ListedSupportingDocument,
  type ListSupportingDocumentsResult,
  type PostSupportingDocumentParams,
  type PostSupportingDocumentResult,
  type PreviousDocumentIdentifier,
} from "../services/patientSupportingDocumentsService";
import type { PatientSupportingDocumentTypeId } from "../types";

export interface PostDocumentByTypeInput {
  patientId: string;
  typeId: PatientSupportingDocumentTypeId;
  contentType: string;
  base64: string;
  description?: string;
  /** Quando presente, o envio vira substituição (relatesTo "replaces"). */
  previousIdentifier?: PreviousDocumentIdentifier;
}

export interface UsePatientSupportingDocumentsServiceResult {
  postDocument: (
    params: PostSupportingDocumentParams,
  ) => Promise<PostSupportingDocumentResult>;
  postDocumentByType: (
    params: PostDocumentByTypeInput,
  ) => Promise<PostSupportingDocumentResult>;
  isPosting: boolean;
  isListing: boolean;
  errorMessage: string | null;
  /** Documentos ativos do último list/refresh. */
  listedDocuments: ListedSupportingDocument[];
  /** Documentos cancelados (histórico) do último list/refresh. */
  cancelledDocuments: ListedSupportingDocument[];
  listDocuments: (patientId: string) => Promise<ListSupportingDocumentsResult>;
  refresh: (patientId: string) => Promise<ListSupportingDocumentsResult>;
  cancelDocument: (documentReferenceId: string) => Promise<void>;
}

/** Referência estável — evita recriar `run` do useAsyncTask a cada render. */
function mapPostError(err: unknown): string {
  return err instanceof Error
    ? err.message
    : "Não foi possível enviar o documento.";
}

function mapListError(err: unknown): string {
  return err instanceof Error
    ? err.message
    : "Não foi possível carregar os documentos.";
}

/**
 * Hook de domínio para documentos de suporte do paciente.
 * UI/legado deve usar este hook — não chamar o service direto.
 */
export function usePatientSupportingDocumentsService(): UsePatientSupportingDocumentsServiceResult {
  const [listedDocuments, setListedDocuments] = useState<
    ListedSupportingDocument[]
  >([]);
  const [cancelledDocuments, setCancelledDocuments] = useState<
    ListedSupportingDocument[]
  >([]);

  const postTask = useCallback(
    (params: PostSupportingDocumentParams) =>
      patientSupportingDocumentsService.postSupportingDocument(params),
    [],
  );

  const postByTypeTask = useCallback(
    (params: PostDocumentByTypeInput) =>
      patientSupportingDocumentsService.postSupportingDocumentByType(params),
    [],
  );

  const listTask = useCallback(async (patientId: string) => {
    const result =
      await patientSupportingDocumentsService.listSupportingDocumentsByPatient({
        patientId,
      });
    setListedDocuments(result.active);
    setCancelledDocuments(result.cancelled);
    return result;
  }, []);

  const {
    run: runPost,
    loading: isPostingRaw,
    error: errorRaw,
  } = useAsyncTask<
    [PostSupportingDocumentParams],
    PostSupportingDocumentResult,
    string
  >({
    task: postTask,
    mapError: mapPostError,
  });

  const {
    run: runPostByType,
    loading: isPostingByType,
    error: errorByType,
  } = useAsyncTask<
    [PostDocumentByTypeInput],
    PostSupportingDocumentResult,
    string
  >({
    task: postByTypeTask,
    mapError: mapPostError,
  });

  const {
    run: runList,
    loading: isListing,
    error: errorList,
  } = useAsyncTask<[string], ListSupportingDocumentsResult, string>({
    task: listTask,
    mapError: mapListError,
  });

  const postDocument = useCallback(
    (params: PostSupportingDocumentParams) => runPost(params),
    [runPost],
  );

  const postDocumentByType = useCallback(
    (params: PostDocumentByTypeInput) => runPostByType(params),
    [runPostByType],
  );

  const listDocuments = useCallback(
    (patientId: string) => runList(patientId),
    [runList],
  );

  const refresh = useCallback(
    (patientId: string) => runList(patientId),
    [runList],
  );

  const cancelDocument = useCallback((documentReferenceId: string) => {
    return patientSupportingDocumentsService.cancelSupportingDocument({
      documentReferenceId,
    });
  }, []);

  return {
    postDocument,
    postDocumentByType,
    isPosting: isPostingRaw || isPostingByType,
    isListing,
    errorMessage: errorRaw ?? errorByType ?? errorList ?? null,
    listedDocuments,
    cancelledDocuments,
    listDocuments,
    refresh,
    cancelDocument,
  };
}
